// src/components/Products.js
// ✅ Guest cart FAB shown ONLY when NOT logged in
// ✅ No TopHeader cart bar — Navbar handles cart display
// ✅ Fires "cartCountUpdated" event so Navbar badge stays in sync
// ✅ Fires "guestCartUpdated" for guest count in Navbar
// ✅ Auto-syncs guest cart → API on login
// ✅ Auto-scroll every 5s per section, stock dot only

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { BASE_URL } from "../API/Constants";

const BADGE_ORDER = ["POPULAR", "NEW_ARRIVAL", "BEST_SELLER", "TRENDING", "LIMITED_OFFER"];
export const GUEST_CART_KEY = "guest_cart";

// ─────────────────────────────────────────────
//  GUEST CART HELPERS
// ─────────────────────────────────────────────
export const guestCart = {
  get() {
    try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "{}"); }
    catch { return {}; }
  },
  set(map) {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(map));
    window.dispatchEvent(new Event("guestCartUpdated"));
  },
  add(productId, product) {
    const cart = guestCart.get();
    const pid = String(productId);
    if (cart[pid]) cart[pid].qty += 1;
    else cart[pid] = { qty: 1, product };
    guestCart.set(cart);
    return cart;
  },
  updateQty(productId, newQty) {
    const cart = guestCart.get();
    const pid = String(productId);
    if (newQty < 1) delete cart[pid];
    else {
      if (!cart[pid]) cart[pid] = { qty: newQty, product: null };
      else cart[pid].qty = newQty;
    }
    guestCart.set(cart);
    return cart;
  },
  remove(productId) {
    const cart = guestCart.get();
    delete cart[String(productId)];
    guestCart.set(cart);
    return cart;
  },
  clear() {
    localStorage.removeItem(GUEST_CART_KEY);
    window.dispatchEvent(new Event("guestCartUpdated"));
  },
  toCartMap(cart) {
    const map = {};
    for (const [pid, row] of Object.entries(cart)) {
      map[pid] = { cartItemId: pid, qty: row.qty };
    }
    return map;
  },
  toArray(cart) {
    return Object.entries(cart).map(([pid, row]) => ({
      productId: pid,
      qty: row.qty,
      product: row.product || {},
    }));
  },
  totalCount(cart) {
    return Object.values(cart).reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
  },
};

// ─────────────────────────────────────────────
//  Broadcast cart count to Navbar
// ─────────────────────────────────────────────
function broadcastCartCount(count) {
  window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: Number(count) || 0 } }));
}

// ─────────────────────────────────────────────
//  IMAGE UTILS
// ─────────────────────────────────────────────
const normalizeImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === "string") {
    const s = images.trim();
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") return [parsed];
    } catch {
      if (s.startsWith("/")) return [s];
      return [s.replace(/^"+|"+$/g, "")];
    }
  }
  return [];
};



// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────
export default function Products() {
  const navigate = useNavigate();

  // Reactive login state — re-checks on token change
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(localStorage.getItem("token")));

  useEffect(() => {
    const check = () => setIsLoggedIn(Boolean(localStorage.getItem("token")));
    window.addEventListener("storage", check);
    const t = setInterval(check, 500);
    return () => { window.removeEventListener("storage", check); clearInterval(t); };
  }, []);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 12;

  const [cartMap, setCartMap] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [toast, setToast] = useState({ msg: "", type: "ok" });

  const IMAGE_ORIGIN = useMemo(() => {
    const b = (BASE_URL || "").trim().replace(/\/+$/, "");
    return b.replace(/\/api$/i, "");
  }, []);

  const imgUrl = useCallback((path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${IMAGE_ORIGIN}${p}`;
    return `${IMAGE_ORIGIN}/${p}`;
  }, [IMAGE_ORIGIN]);

  const money = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return Number.isNaN(n) ? String(v) : n.toFixed(2);
  };

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast({ msg: "", type: "ok" }), 1800);
  };

  // ─── LOAD PRODUCTS ───
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axiosInstance.get(`/api/products?page=${page}&limit=${limit}`);
        const data = res.data || {};
        const list = Array.isArray(data) ? data : data?.products || [];
        if (!mounted) return;
        setProducts(list);
        setTotalPages(data?.totalPages || 1);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [page]);

  // ─── LOAD CART ───
  const loadCart = useCallback(async () => {
    if (!isLoggedIn) {
      const raw = guestCart.get();
      const map = guestCart.toCartMap(raw);
      setCartMap(map);
      // Also broadcast guest count to Navbar
      broadcastCartCount(guestCart.totalCount(raw));
      return;
    }
    try {
      const res = await axiosInstance.get("/api/cart");
      const d = res.data || {};
      const items = (d.CartItems || d.cartItems || d.items || []) ?? [];
      const next = {};
      for (const it of items) {
        const prod = it?.product || it?.Product || it?.productDetails || {};
        const pid = prod?.id ?? it?.productId ?? it?.ProductId ?? it?.product_id;
        const cartItemId = it?.id ?? it?.cartItemId ?? it?._id;
        const qty = Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1);
        if (pid != null) next[String(pid)] = { cartItemId, qty };
      }
      setCartMap(next);
      // Broadcast total count to Navbar
      const total = Object.values(next).reduce((s, r) => s + (Number(r?.qty) || 0), 0);
      broadcastCartCount(total);
    } catch { /* ignore */ }
  }, [isLoggedIn]);

  useEffect(() => { loadCart(); }, [loadCart]);

  // ─── AUTO-SYNC guest cart → API on login ───
  const prevLoggedIn = useRef(false);
  useEffect(() => {
    if (isLoggedIn && !prevLoggedIn.current) {
      // Just transitioned to logged in — sync guest cart
      const guestItems = guestCart.toArray(guestCart.get());
      if (guestItems.length > 0) {
        (async () => {
          try {
            for (const item of guestItems) {
              if (!item.productId) continue;
              await axiosInstance.post("/api/cart", { productId: item.productId, qty: item.qty });
            }
            guestCart.clear();
            await loadCart();
            showToast(`✅ ${guestItems.length} item${guestItems.length > 1 ? "s" : ""} synced to your cart`, "ok");
          } catch { /* silent */ }
        })();
      } else {
        loadCart();
      }
    }
    prevLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, loadCart]);

  // ─── Guest cart listener ───
  useEffect(() => {
    const onUpdate = () => {
      if (!isLoggedIn) {
        const raw = guestCart.get();
        setCartMap(guestCart.toCartMap(raw));
        broadcastCartCount(guestCart.totalCount(raw));
      }
    };
    window.addEventListener("guestCartUpdated", onUpdate);
    return () => window.removeEventListener("guestCartUpdated", onUpdate);
  }, [isLoggedIn]);

  // ─── Guest cart total count for FAB ───
  const guestCartCount = useMemo(() => {
    if (isLoggedIn) return 0;
    return guestCart.totalCount(guestCart.get());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartMap, isLoggedIn]);

  // ─── ADD TO CART ───
  const addToCart = async (e, productId, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      const updated = guestCart.add(productId, product);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      broadcastCartCount(guestCart.totalCount(updated));
      showToast("🛒 Added to cart", "ok");
      return;
    }
    try {
      setBusyKey(`add:${productId}`);
      await axiosInstance.post("/api/cart", { productId });
      await loadCart();
      showToast("✅ Added to cart", "ok");
    } catch (er) {
      showToast(`❌ ${er?.response?.data?.msg || er?.response?.data?.message || er?.message || "Failed"}`, "err");
    } finally {
      setBusyKey("");
    }
  };

  // ─── UPDATE QTY ───
  const updateCartQty = async (e, productId, newQty, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      const updated = guestCart.updateQty(productId, newQty);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      broadcastCartCount(guestCart.totalCount(updated));
      return;
    }
    const pid = String(productId);
    const row = cartMap[pid];
    if (!row?.cartItemId) {
      if (newQty >= 1) return addToCart(e, productId, product);
      return;
    }
    if (newQty < 1) {
      try {
        setBusyKey(`qty:${productId}`);
        await axiosInstance.delete(`/api/cart/${row.cartItemId}`);
        setCartMap((prev) => {
          const next = { ...prev };
          delete next[pid];
          const total = Object.values(next).reduce((s, r) => s + (Number(r?.qty) || 0), 0);
          broadcastCartCount(total);
          return next;
        });
        showToast("🗑 Item removed", "ok");
      } catch (er) {
        showToast(`❌ ${er?.response?.data?.msg || er?.message || "Failed removal"}`, "err");
        await loadCart();
      } finally {
        setBusyKey("");
      }
      return;
    }
    try {
      setBusyKey(`qty:${productId}`);
      await axiosInstance.put(`/api/cart/${row.cartItemId}`, { qty: newQty });
      setCartMap((prev) => {
        const next = { ...prev, [pid]: { ...prev[pid], qty: newQty } };
        const total = Object.values(next).reduce((s, r) => s + (Number(r?.qty) || 0), 0);
        broadcastCartCount(total);
        return next;
      });
    } catch (er) {
      showToast(`❌ ${er?.response?.data?.msg || er?.message || "Failed"}`, "err");
      await loadCart();
    } finally {
      setBusyKey("");
    }
  };

  // ─── GROUP BY BADGE ───
  const grouped = useMemo(() => {
    const active = (products || []).filter((p) => p?.isActive !== false);
    const map = new Map();
    for (const p of active) {
      const b = p?.badge || "OTHER";
      if (!map.has(b)) map.set(b, []);
      map.get(b).push(p);
    }
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      map.set(k, arr);
    }
    const orderedKeys = [
      ...BADGE_ORDER.filter((k) => map.has(k)),
      ...Array.from(map.keys()).filter((k) => !BADGE_ORDER.includes(k)),
    ];
    return orderedKeys.map((badge) => ({ badge, items: map.get(badge) || [] }));
  }, [products]);

  return (
    <div className="pp-page">
      <style>{css}</style>

      <div className="pp-container">
        {toast.msg && (
          <div className={`pp-toast${toast.type === "err" ? " pp-toast--err" : ""}`}>
            {toast.msg}
          </div>
        )}

        {loading && <div className="pp-msg">Loading…</div>}
        {err && <div className="pp-msg pp-err">{err}</div>}
        {!loading && !err && grouped.length === 0 && <div className="pp-msg">No products found.</div>}

        {!loading && !err && grouped.map((section) => (
          <ProductSection
            key={section.badge}
            title={prettyBadge(section.badge)}
            badge={section.badge}
            items={section.items}
            money={money}
            imgUrl={imgUrl}
            cartMap={cartMap}
            busyKey={busyKey}
            onAdd={addToCart}
            onQty={updateCartQty}
            onOpen={(id) => navigate(`/ProductDetails/${id}`)}
          />
        ))}

        {!loading && !err && totalPages > 1 && (
          <div className="pp-pagination">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="pp-pageBtn">Prev</button>
            <span className="pp-pageInfo">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="pp-pageBtn">Next</button>
          </div>
        )}
      </div>


    </div>
  );
}

// ─────────────────────────────────────────────
//  PRODUCT SECTION
// ─────────────────────────────────────────────
function ProductSection({ title, badge, items, money, imgUrl, cartMap, busyKey, onAdd, onQty, onOpen }) {
  const scrollerRef = useRef(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !items || items.length === 0) return;
    const getStep = () => {
      const card = el.querySelector(".pp-cardBtn");
      const w = card?.getBoundingClientRect()?.width || 170;
      return w * 2 + 12;
    };
    const tick = () => {
      const max = el.scrollWidth - el.clientWidth - 2;
      if (max <= 0) return;
      if (el.scrollLeft >= max) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: getStep(), behavior: "smooth" });
    };
    const t = window.setInterval(tick, 5000);
    return () => window.clearInterval(t);
  }, [badge, items]);

  if (!items || items.length === 0) return null;

  const scrollByCards = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".pp-cardBtn");
    const step = (card?.getBoundingClientRect()?.width || 170) * 2 + 12;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="pp-section">
      <div className="pp-sectionHead">
        <div className="pp-sectionTitle">{title}</div>
        <div className="pp-sectionLine" />
        <div className="pp-headRight">
          <button type="button" className="pp-arrowBtn ghost" onClick={() => scrollByCards(-1)} aria-label="Scroll left">‹</button>
          <button type="button" className="pp-arrowBtn" onClick={() => scrollByCards(1)} aria-label="Scroll right">›</button>
        </div>
      </div>

      <div className="pp-rowWrap" ref={scrollerRef}>
        {items.map((p) => {
          const inStock = Number(p?.stockQty || 0) > 0;
          const pid = String(p?.id);
          const row = cartMap?.[pid];
          const qty = Number(row?.qty || 0);
          const isBusy = busyKey === `add:${p.id}` || busyKey === `qty:${p.id}`;
          const imgs = normalizeImages(p?.images);
          const image = imgUrl(imgs?.[0] || p?.image);

          return (
            <div
              key={p.id}
              className="pp-cardBtn"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(p.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(p.id); }}
            >
              <div className="pp-card">
                <div className="pp-imgWrap">
                  <img
                    src={image || "https://via.placeholder.com/600x400?text=No+Image"}
                    alt={p?.name || "Product"}
                    loading="lazy"
                    onError={(e) => {
                      if (e.currentTarget.src !== "https://via.placeholder.com/600x400?text=No+Image")
                        e.currentTarget.src = "https://via.placeholder.com/600x400?text=No+Image";
                    }}
                  />

                </div>

                <div className="pp-body">
                  <div className="pp-name" title={p?.name}>{p?.name}</div>

                  <div className="pp-stars">
                    {[1, 2, 3, 4].map(i => (
                      <svg key={i} className="star-filled" viewBox="0 0 24 24" width="12" height="12">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                    <svg className="star-empty" viewBox="0 0 24 24" width="12" height="12">
                      <path d="M22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                    </svg>
                  </div>

                  <div className="pp-priceRow">
                    <div className="pp-price">₹ {money(p?.price)}</div>
                    {p?.mrp ? <div className="pp-mrp">₹ {money(p?.mrp)}</div> : <div />}
                  </div>

                  <div className="pp-controls">
                    {!inStock ? (
                      <div className="pp-oos">Out of stock</div>
                    ) : qty <= 0 ? (
                      <button type="button" className="pp-addBtn" onClick={(e) => onAdd(e, p.id, p)} disabled={isBusy}>
                        {isBusy ? "Adding…" : "ADD"}
                      </button>
                    ) : (
                      <div className="pp-qtyBox" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <button type="button" className="pp-qtyBtn" disabled={isBusy} onClick={(e) => onQty(e, p.id, qty - 1, p)}>−</button>
                        <div className="pp-qtyVal">{qty}</div>
                        <button type="button" className="pp-qtyBtn" disabled={isBusy} onClick={(e) => onQty(e, p.id, qty + 1, p)}>+</button>
                      </div>
                    )}
                  </div>

                  <div className="pp-tap">Tap for details</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
function prettyBadge(b) {
  if (!b) return "Items";
  return String(b).replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─────────────────────────────────────────────
//  CSS
// ─────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
  
  * { box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body {
    margin: 0;
    background: #ffffff !important;
    font-family: 'Montserrat', sans-serif;
    color: #222;
  }
  :root {
    --bg: #ffffff;
    --panel: transparent;
    --card: #ffffff;
    --stroke: #eaeaea;
    --stroke2: #eaeaea;
    --text: #222;
    --muted: #888;
    --nav: #0F1E3D;
    --acc: #C9A84C;
    --acc2: #1ECCB0;
  }

  .pp-page { min-height: 100vh; background: var(--bg); padding: 18px 0 80px; font-family: 'Montserrat', sans-serif;}
  .pp-container { width: min(1400px, 100%); margin: 0 auto; padding: 0 16px; }

  .pp-toast {
    position: sticky; top: 10px; z-index: 9; margin-bottom: 14px;
    padding: 10px 14px; border-radius: 12px; background: #ffffff;
    border: 1px solid var(--stroke); box-shadow: 0 4px 12px rgba(0,0,0,.05);
    color: var(--text); font-weight: 700;
  }
  .pp-toast--err {
    background: rgba(239,68,68,.06);
    border-color: rgba(239,68,68,.25);
    color: #cb2424;
  }
  .pp-msg {
    margin: 12px 0; padding: 12px 14px; border-radius: 12px;
    background: #ffffff; border: 1px solid var(--stroke);
    box-shadow: 0 4px 12px rgba(0,0,0,.04); color: var(--text); font-weight: 700;
  }
  .pp-err { border-color: rgba(239,68,68,.25); background: rgba(239,68,68,.06); color: #cb2424; }

  .pp-section { margin-top: 18px; }
  .pp-sectionHead { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 0 2px; }
  .pp-sectionTitle { font-size: 17px; font-weight: 700; color: var(--text); white-space: nowrap; }
  
  .pp-sectionLine {
    height: 1.5px;
    flex: 1;
    background: linear-gradient(90deg, rgba(0,0,0,.10), rgba(0,0,0,0));
  }

  .pp-headRight { display: flex; gap: 8px; align-items: center; }
  .pp-arrowBtn {
    width: 28px; height: 28px; border-radius: 50%;
    border: 1px solid var(--stroke); background: #ffffff; color: var(--muted);
    font-weight: 500; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,.02);
    display: flex; justify-content: center; align-items: center;
    line-height: 1; font-size: 16px; transition: background 0.15s;
  }
  .pp-arrowBtn:hover { background: #f8fafc; color: var(--text); }
  .pp-arrowBtn.ghost { background: #ffffff; }

  .pp-rowWrap {
    margin-top: 14px; padding: 4px 0 16px 0;
    display: flex; gap: 10px; overflow-x: auto; overflow-y: hidden;
    scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch;
    background: transparent; border: none; box-shadow: none; border-radius: 0;
  }
  .pp-rowWrap::-webkit-scrollbar { display: none; }

  .pp-cardBtn {
    flex: 0 0 auto; cursor: pointer; border-radius: 12px;
    outline: none; scroll-snap-align: start; width: 150px;
  }
  @media (max-width: 640px) {
    .pp-rowWrap { gap: 10px; }
    .pp-cardBtn { width: calc((100vw - 32px - 20px) / 2.6); }
  }
  .pp-cardBtn:focus { outline: 2px solid rgba(59,130,246,.35); outline-offset: 2px; }

  .pp-card {
    height: 100%; border-radius: 12px; overflow: hidden;
    border: 1px solid var(--stroke); background: var(--card);
    box-shadow: 0 4px 12px rgba(0,0,0,.04); display: flex; flex-direction: column;
  }
  .pp-imgWrap {
    position: relative; height: 130px;
    background: #ffffff; padding: 8px;
  }
  .pp-imgWrap img { width: 100%; height: 100%; object-fit: contain; display: block; }

  .pp-body {
    padding: 8px 10px 12px 10px; display: flex; flex-direction: column;
    gap: 6px; color: var(--text); flex: 1; justify-content: space-between;
  }
  .pp-name {
    font-size: 13px; font-weight: 700; line-height: 1.25; min-height: 32px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    text-align: left;
  }
  .pp-stars{ display: flex; gap: 2px; }
  .star-filled{ fill: #f39c12; }
  .star-empty{ fill: rgba(0,0,0,.15); }

  .pp-priceRow { display: flex; justify-content: flex-start; align-items: baseline; gap: 6px; }
  .pp-price { font-size: 15px; font-weight: 700; color: #111; }
  .pp-mrp { font-size: 11px; font-weight: 600; color: rgba(15,23,42,.45); text-decoration: line-through; }

  .pp-controls { margin-top: 4px; }
  .pp-addBtn {
    width: 100%; padding: 8px; border-radius: 20px;
    border: none; background: #006600;
    color: #ffffff; font-weight: 700; font-size: 11px; cursor: pointer; transition: background 0.15s;
    font-family: inherit; letter-spacing: 0.3px; text-transform: uppercase;
  }
  .pp-addBtn:hover:not(:disabled) { background: #004d00; }
  .pp-addBtn:disabled { opacity: 0.6; cursor: not-allowed; }

  .pp-qtyBox {
    width: 100%; display: grid; grid-template-columns: 32px 1fr 32px; align-items: center;
    border-radius: 20px; overflow: hidden; border: none; background: #006600;
  }
  .pp-qtyBtn {
    height: 32px; border: none; background: transparent;
    color: #ffffff; font-size: 18px; font-weight: 700; cursor: pointer;
    font-family: inherit; display: flex; justify-content: center; align-items: center;
  }
  .pp-qtyBtn:hover { background: rgba(0,0,0,0.15); }
  .pp-qtyBtn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pp-qtyVal { height: 32px; display: grid; place-items: center; font-weight: 700; font-size: 12px; color: #ffffff; }

  .pp-oos {
    width: 100%; padding: 8px; border-radius: 20px;
    border: none; background: rgba(239,68,68,.1);
    color: #ef4444; font-weight: 700; font-size: 11px; text-align: center; text-transform: uppercase;
  }
  .pp-tap { display: none; }

  .pp-pagination { display: flex; justify-content: center; align-items: center; gap: 16px; margin-top: 30px; padding-bottom: 20px; }
  .pp-pageBtn {
    padding: 10px 20px; border-radius: 12px; border: 1px solid var(--stroke);
    background: #ffffff; color: var(--text); font-size: 14px; font-weight: 700;
    cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,.03); transition: all 0.2s ease;
    font-family: inherit;
  }
  .pp-pageBtn:hover:not(:disabled) { background: #f8fafc; border-color: var(--stroke2); }
  .pp-pageBtn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pp-pageInfo { font-size: 14px; font-weight: 700; color: var(--text); }


`;